/**
 * Extra esperienze: catalogo (JSON su excursions.extras) + risoluzione righe/totale.
 * price_type: 'per_booking' (flat) | 'per_person' (× partecipanti).
 *
 * Modello pricing canonico (kind):
 *   'service' (default) — soggetto a MARKUP e a SCONTO (se coupon/LM lo prevede).
 *                          Entra in markupableVendor e in discountBase.
 *   'tax'               — pass-through INTERO (no markup, no sconto, no coupon).
 *                          Aggiunto al totale cliente come voce separata.
 *                          Default lato saldo/imbarco (offline).
 */

export type ExcursionExtraPriceType = 'per_booking' | 'per_person';
export type ExcursionExtraKind = 'service' | 'tax';

export interface ExcursionExtra {
  key: string;
  label: string;
  price: number;
  price_type: ExcursionExtraPriceType;
  /**
   * Tipo extra (modello pricing).
   * 'service' = markup SÌ + sconto SÌ (se coupon lo prevede) — default per retrocompat.
   * 'tax'     = pass-through: nessun markup, nessun sconto, pagato offline al porto.
   */
  kind?: ExcursionExtraKind;
  mandatory?: boolean;
  description?: string;
}

export interface ResolvedExtraLine {
  extra_key: string;
  label: string;
  price_type: ExcursionExtraPriceType;
  /** Tipo extra per il modello pricing (default 'service'). */
  kind: ExcursionExtraKind;
  unit_price: number;
  quantity: number;
  total_price: number;
}

function num(v: unknown, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Normalizza il catalogo JSON in ExcursionExtra[] valido. */
export function sanitizeExcursionExtras(raw: unknown): ExcursionExtra[] {
  const arr = Array.isArray(raw) ? raw : [];
  const out: ExcursionExtra[] = [];
  const seen = new Set<string>();
  for (const e of arr) {
    if (!e || typeof e !== 'object') continue;
    const r = e as Record<string, unknown>;
    const key = String(r.key || '').trim();
    const label = String(r.label || '').trim();
    if (!key || !label || seen.has(key)) continue;
    seen.add(key);
    out.push({
      key: key.slice(0, 80),
      label: label.slice(0, 150),
      price: Math.max(0, num(r.price)),
      price_type: r.price_type === 'per_person' ? 'per_person' : 'per_booking',
      kind: r.kind === 'tax' ? 'tax' : 'service',
      mandatory: !!r.mandatory,
      description: r.description ? String(r.description).slice(0, 500) : undefined,
    });
  }
  return out;
}

export const mandatoryExtraKeys = (catalog: ExcursionExtra[]): string[] =>
  catalog.filter((e) => e.mandatory).map((e) => e.key);

/**
 * Risolve le righe extra per una prenotazione: i mandatory sono sempre inclusi,
 * più quelli selezionati. Ritorna righe (snapshot) + totale vendor.
 * Mantiene il campo `kind` per la logica pricing downstream.
 */
export function resolveExcursionExtras(
  catalog: ExcursionExtra[],
  selectedKeys: string[],
  people: number,
): { lines: ResolvedExtraLine[]; vendorTotal: number } {
  const ppl = Math.max(1, Math.floor(num(people, 1)));
  const wanted = new Set([...selectedKeys, ...mandatoryExtraKeys(catalog)]);
  const lines: ResolvedExtraLine[] = [];
  let vendorTotal = 0;
  for (const e of catalog) {
    if (!wanted.has(e.key)) continue;
    const quantity = e.price_type === 'per_person' ? ppl : 1;
    const total = Math.round(e.price * quantity * 100) / 100;
    lines.push({
      extra_key: e.key,
      label: e.label,
      price_type: e.price_type,
      kind: e.kind ?? 'service',
      unit_price: e.price,
      quantity,
      total_price: total,
    });
    vendorTotal += total;
  }
  return { lines, vendorTotal: Math.round(vendorTotal * 100) / 100 };
}
